import React, { useState, useEffect, useRef } from 'react';
import { Asset, AssetType } from '@/firebase/assetService';
import { locationService, Location } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useNotification } from '@/contexts/NotificationContext';
import { usePreview } from '@/contexts/PreviewContext';
import { UserRolePicker } from './UserRolePicker';
import { Upload, X, Sparkles } from 'lucide-react';
import { storage } from '@/firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  createStorageMetadata,
  recordDocumentMetadata,
  type DocumentUploadContext,
} from '@/firebase/documentRegistry';
import { useAuth } from '@/contexts/AuthContext';
import { sanitizeFileName } from '@/utils/fileNameSanitizer';

interface AssetFormProps {
  assetType: AssetType;
  onSubmit: () => void;
  onCancel: () => void;
  stayOpenAfterSubmit?: boolean; // If true, form stays open after successful submission
  onSuccess?: () => void; // Optional callback called after successful submission (even if staying open)
  onBulkUploadClick?: () => void; // Optional callback to switch to bulk upload mode
}

const AssetForm: React.FC<AssetFormProps> = ({ assetType, onSubmit, onCancel, stayOpenAfterSubmit = false, onSuccess, onBulkUploadClick }) => {
  const { currentOrganization } = useOrganization();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const { previewState, isPreviewMode } = usePreview();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [assetName, setAssetName] = useState('');
  const [assetLocation, setAssetLocation] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  useEffect(() => {
    loadLocations();
  }, [currentOrganization?.id, isPreviewMode, previewState?.previewType, previewState?.previewUserId]);

  const loadLocations = async () => {
    setIsLoadingLocations(true);
    try {
      let effectiveUserId: string | undefined;
      let userLocationIds: string[] | undefined;
      let userRole: string | undefined;

      if (isPreviewMode && previewState) {
        if (previewState.previewType === 'user' && previewState.previewUserId) {
          effectiveUserId = previewState.previewUserId;
          userRole = previewState.role;
          userLocationIds = previewState.locationIds;
        } else {
          effectiveUserId = user?.id ?? '';
          userRole = 'admin';
          userLocationIds = undefined;
        }
      } else if (user?.id) {
        effectiveUserId = user.id;
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data() as any;
            userLocationIds = userData?.locationIds;
            userRole = userData?.role;
          }
        } catch (error) {
          console.error('Error fetching user data for locations:', error);
        }
      }

      const result = await locationService.getAccessibleLocations(
        currentOrganization?.id,
        effectiveUserId,
        userRole,
        userLocationIds
      );
      if (result.success && result.data) {
        const validLocations = result.data.filter(loc => loc.id && loc.name);
        setLocations(validLocations);

        // Auto-select location if only one available or previously selected location no longer accessible
        if (validLocations.length === 1 && !selectedLocationId) {
          setSelectedLocationId(validLocations[0].id || '');
        } else if (
          selectedLocationId &&
          !validLocations.some(location => location.id === selectedLocationId)
        ) {
          setSelectedLocationId('');
        }
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error('Error loading accessible locations:', error);
      setLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setPhotoFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotoPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        showNotification('Please select an image file', 'error');
      }
    }
  };

  const uploadPhoto = async (): Promise<string | null> => {
    if (!photoFile) return null;

    try {
      setIsUploading(true);
      
      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFileName(photoFile.name);
      const fileName = `assets/${timestamp}_${sanitizedFileName}`;
      
      console.log('[AssetForm] Filename sanitization', {
        original: photoFile.name,
        sanitized: sanitizedFileName,
        finalPath: fileName,
      });
      
      const storageRef = ref(storage, fileName);
      const uploadedAt = new Date().toISOString();
      const storageContext: DocumentUploadContext = {
        docType: 'asset_photo',
        organizationId: currentOrganization?.id,
        locationId: selectedLocationId,
        assetId: undefined,
        uploadedBy: user
          ? { id: user.id, email: user.email, name: user.name }
          : undefined,
        title: `${assetName || ''} ${photoFile.name}`.trim() || photoFile.name,
        source: 'web',
        additionalMetadata: {
          assetTypeId: assetType.id,
          assetTypeName: assetType.name,
        },
      };
      await uploadBytes(
        storageRef,
        photoFile,
        createStorageMetadata(storageContext, {
          contentType: photoFile.type,
          uploadedAt,
        })
      );
      const url = await getDownloadURL(storageRef);
      await recordDocumentMetadata({
        storagePath: storageRef.fullPath,
        bucket: (storageRef as any).bucket,
        downloadUrl: url,
        mimeType: photoFile.type,
        sizeBytes: photoFile.size,
        uploadedAt,
        context: storageContext,
      });
      return url;
    } catch (error: any) {
      console.error('Error uploading photo:', error);
      showNotification(`Error uploading photo: ${error.message}`, 'error');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLocationId) {
      showNotification('Please select a location', 'error');
      return;
    }
    
    if (!assetName.trim()) {
      showNotification('Please enter an asset name', 'error');
      return;
    }

    // Validate required custom fields
    if (assetType.customFields && assetType.customFields.length > 0) {
      for (const field of assetType.customFields) {
        if (field.required && !fieldValues[field.name]?.trim()) {
          showNotification(`Please fill in the required field: ${field.name}`, 'error');
          return;
        }
      }
    }

    setIsUploading(true);
    try {
      const photoUrl = await uploadPhoto();
      
      // Clean fieldValues - only include non-empty values
      const cleanedFieldValues: Record<string, string> = {};
      if (assetType.customFields && assetType.customFields.length > 0) {
        assetType.customFields.forEach(field => {
          const value = fieldValues[field.name]?.trim();
          if (value) {
            cleanedFieldValues[field.name] = value;
          }
        });
      }
      
      const assetData: Omit<Asset, 'id'> = {
        assetTypeId: assetType.id!,
        locationId: selectedLocationId,
        assetName: assetName.trim(),
        assetLocation: assetLocation.trim() || undefined,
        photoUrl: photoUrl || undefined,
        photoFileName: photoFile?.name || undefined,
        fieldValues: Object.keys(cleanedFieldValues).length > 0 ? cleanedFieldValues : undefined,
        ...(currentOrganization?.id && { organizationId: currentOrganization.id })
      };

      // Import assetService dynamically to avoid circular dependency
      const { assetService } = await import('@/firebase/assetService');
      // Get user info for audit log
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await assetService.addAsset(assetData, currentOrganization?.id, userInfo);
      
      if (result.success) {
        // Track asset creation
        const { trackAsset } = await import('@/utils/analytics');
        trackAsset.created(assetType.name, selectedLocationId || undefined);
        
        showNotification('Asset added successfully', 'success');
        
        // Call onSuccess callback if provided (for refreshing data)
        if (onSuccess) {
          onSuccess();
        }
        
        if (stayOpenAfterSubmit) {
          // Clear form fields but keep location selected
          setAssetName('');
          setAssetLocation('');
          setPhotoFile(null);
          setPhotoPreview(null);
          setFieldValues({});
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          // Don't call onSubmit() - keep form open
        } else {
          // Original behavior - close form
          onSubmit();
        }
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Bulk Upload CTA */}
      {onBulkUploadClick && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-purple-900 mb-0.5">Adding multiple assets?</p>
              <p className="text-xs text-purple-700">Upload CSV or PDF — AI extracts and adds them for you.</p>
            </div>
            <button
              type="button"
              onClick={onBulkUploadClick}
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Add with AI
            </button>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Asset Type
        </label>
        <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900">
          {assetType.name}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Location <span className="text-red-500">*</span>
        </label>
        <select
          value={selectedLocationId}
          onChange={(e) => setSelectedLocationId(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          required
          disabled={isLoadingLocations || locations.length === 0}
        >
          <option value="">{isLoadingLocations ? 'Loading locations...' : 'Select a location...'}</option>
          {locations.map(location => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
        {!isLoadingLocations && locations.length === 0 && (
          <p className="text-xs text-red-600 mt-2">
            No locations available. You may not be assigned to any locations or locations have not been created.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Asset Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={assetName}
          onChange={(e) => setAssetName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Fire Extinguisher #1"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Asset Location (Optional)
        </label>
        <input
          type="text"
          value={assetLocation}
          onChange={(e) => setAssetLocation(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="e.g., Room 101, Front Desk"
        />
      </div>

      {/* Custom Fields */}
      {assetType.customFields && assetType.customFields.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Custom Fields</h3>
          <div className="space-y-4">
            {assetType.customFields.map((field, index) => (
              <div key={field.id || index}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.name}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {field.description && (
                  <p className="text-xs text-gray-500 mb-1">{field.description}</p>
                )}
                {field.type === 'loan_officer_picker' ? (
                  <UserRolePicker
                    field={field}
                    value={fieldValues[field.name]}
                    onChange={(userId) => setFieldValues({ ...fieldValues, [field.name]: userId || '' })}
                    required={field.required}
                  />
                ) : field.type === 'alphanumeric' ? (
                  <input
                    type="text"
                    value={fieldValues[field.name] || ''}
                    onChange={(e) => {
                      // Validate alphanumeric input
                      const value = e.target.value;
                      const pattern = /^[A-Za-z0-9]*$/;
                      if (pattern.test(value) || value === '') {
                        setFieldValues({ ...fieldValues, [field.name]: value });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={`Enter ${field.name.toLowerCase()} (letters and numbers only)`}
                    required={field.required}
                    pattern={field.metadata?.validation?.pattern || '^[A-Za-z0-9]+$'}
                  />
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={fieldValues[field.name] || ''}
                    onChange={(e) => setFieldValues({ ...fieldValues, [field.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={`Enter ${field.name.toLowerCase()}`}
                    rows={3}
                    required={field.required}
                  />
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 
                          field.type === 'date' ? 'date' : 
                          field.type === 'email' ? 'email' : 
                          field.type === 'tel' ? 'tel' : 'text'}
                    value={fieldValues[field.name] || ''}
                    onChange={(e) => setFieldValues({ ...fieldValues, [field.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={`Enter ${field.name.toLowerCase()}`}
                    required={field.required}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Photo (Optional)
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoSelect}
          className="hidden"
        />
        
        {photoPreview ? (
          <div className="relative">
            <img src={photoPreview} alt="Preview" className="w-full h-48 object-contain border border-gray-300 rounded-lg" />
            <button
              type="button"
              onClick={() => {
                setPhotoPreview(null);
                setPhotoFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors flex items-center justify-center gap-2"
          >
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-sm text-gray-600">Click to upload photo</span>
          </button>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          disabled={isUploading}
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isUploading}
        >
          {isUploading ? 'Uploading...' : 'Save Asset'}
        </button>
      </div>
    </div>
  );
};

export default AssetForm;

